/**
 * Single source of truth for Vite env vars on the client.
 * Mirrors the backend pattern in `server/shared/env.config.js`.
 *
 * Consumers should import from here, not from `import.meta.env` directly,
 * so the `VITE_` prefix and any cross-cutting derivations live in one place.
 *
 * Only vars actually referenced in `src/` are re-exported — extend as needed.
 */

type DbEnvironment = 'stage' | 'production';

export const PORTMONE_MERCHANT_ID = import.meta.env.VITE_PORTMONE_MERCHANT_ID as string;
export const PUBLIC_CAPTCHA_KEY = import.meta.env.VITE_PUBLIC_CAPTCHA_KEY as string;

export const DB_ENVIRONMENT: DbEnvironment =
    (import.meta.env.VITE_DB_ENVIRONMENT as DbEnvironment) ?? 'stage';
    
export const IS_PROD_DB = DB_ENVIRONMENT === 'production';

/**
 * Which deployment the page is running on, derived from its own URL.
 *
 * These live here rather than in `constants.ts` for one reason: they are pure
 * functions of a string, so they can be tested. The values in `constants.ts` used
 * to read `window.location` inline at module load, which made every one of them
 * untestable and — because almost everything imports from that file — made the
 * whole app unloadable in a plain Node process. Tests worked around it with a
 * `window` stub in `vitest.setup.ts`.
 *
 * Substring matching throughout, deliberately. `isProdHost` is `true` for both
 * `pay.prostopay.net` and `qa.pay.prostopay.net`, because QA runs against the
 * production database.
 */
export const isLocalHost = (origin: string): boolean =>
    origin.includes('localhost');

export const isStageHost = (host: string): boolean =>
    host.includes('hunter-dev');

export const isProdHost = (hostname: string): boolean =>
    hostname.includes('pay.prostopay.net');

/**
 * `window.location`, or empty strings when there is no `window`.
 *
 * The app only ever runs in a browser; this exists so that importing a module —
 * any module, since `constants.ts` is imported almost everywhere — does not throw
 * in Node. Every predicate above then answers `false`, which is the right neutral
 * default for a process that is not a deployment.
 */
export const readLocation = (): Pick<
    Location,
    'origin' | 'host' | 'hostname'
> =>
    typeof window === 'undefined'
        ? { origin: '', host: '', hostname: '' }
        : window.location;
